// Pure redaction utility for log context.
// Strips obvious secret-shaped fields before anything is written to a sink.
// See react-rewrite-plan.md section 8.1.

const SECRET_KEY_PATTERN = /token|secret|password|cookie|authorization|api[-_]?key|bearer/i;
const REDACTED = '[redacted]';
const MAX_DEPTH = 4;

// True for objects we should recurse into. Arrays, Errors, Dates, and other
// non-plain objects are returned as-is to keep behavior predictable.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function redactValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_DEPTH) {
    return value;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return redactObject(value, depth);
}

function redactObject(
  input: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (SECRET_KEY_PATTERN.test(key)) {
      output[key] = REDACTED;
      continue;
    }
    output[key] = redactValue(value, depth + 1);
  }
  return output;
}

// Returns a redacted copy. Never mutates the input.
export function redactContext<T extends Record<string, unknown> | undefined>(
  context: T,
): T {
  if (context === undefined || context === null) {
    return context;
  }
  return redactObject(context, 0) as T;
}
